// BracePlanView.tsx — Brace Plan view compositor.
// Layout: BraceSidebar | BraceViewport3D | BracePropsPanel
// Shows one floor at a time; floor switcher tabs at top of sidebar.
// Segment building mirrors SiteViewport3D (per-floor chain + direction-based standalone placement).

import { useState, useCallback, useMemo, useEffect } from 'react';
import { useShallowStore } from '../store/useStore';
import BraceSidebar, { type ElevationGroup } from './BraceSidebar';
import BraceViewport3D from './BraceViewport3D';
import BracePlan2D from './BracePlan2D';
import BracePropsPanel from './BracePropsPanel';
import ResizeHandle from './ResizeHandle';
import { computeFootprint, wallWidth, DIR_ANGLE } from '../utils/buildingFootprint';
import { cornerJointWidth } from '../types/panelvar';
import type { Elevation, Floor, PanelData } from '../types/panelvar';
import type { WallSegment } from './SiteViewport3D';
import { computeBraces } from '../utils/computeBraces';

// ── Floor base-elevation calc (mirrors SiteViewport3D) ──────────────────────
function computeFloorBaseElevations(floors: Floor[], allElevations: Elevation[], panels: PanelData[]): Map<string, number> {
  const result = new Map<string, number>();
  let y = 0;
  for (const floor of floors) {
    if (floor.type === 'mezzanine') { result.set(floor.id, floor.ledgerHeight ?? y); continue; }
    result.set(floor.id, y);
    const floorElevs = allElevations.filter(e => floor.elevationIds.includes(e.id));
    const floorPanelIds = new Set(floorElevs.flatMap(e => e.panelIds));
    const maxH = Math.max(0, ...panels.filter(p => floorPanelIds.has(p.id)).map(p => p.mpvar.height));
    if (maxH > 0) y += maxH + (floor.slabThickness ?? 5);
  }
  return result;
}

// ── Build segments for one floor — chain + direction-based standalone ────────
function buildFloorSegments(
  floorElevs: Elevation[],
  allPanels: PanelData[],
  jointWidth: number,
  baseElevation: number,
  floorId: string,
): WallSegment[] {
  if (floorElevs.length === 0) return [];
  const resolvePanels = (e: Elevation): PanelData[] =>
    e.panelIds.map(id => allPanels.find(p => p.id === id)).filter((p): p is PanelData => p != null);

  const footprint = computeFootprint(floorElevs, allPanels, jointWidth);
  const result: WallSegment[] = footprint.walls.map(w => ({
    elevId: w.elevId, name: w.name, panels: w.panels,
    x: w.startX, z: w.startY, rotationY: w.angle,
    leftEndJoint: w.leftEndJoint, rightEndJoint: w.rightEndJoint,
    baseElevation, floorId,
  }));

  const visited = new Set(footprint.walls.map(w => w.elevId));
  let chainMinX = 0, chainMaxX = 0, chainMinZ = 0, chainMaxZ = 0;
  for (const w of footprint.walls) {
    chainMinX = Math.min(chainMinX, w.startX, w.endX);
    chainMaxX = Math.max(chainMaxX, w.startX, w.endX);
    chainMinZ = Math.min(chainMinZ, w.startY, w.endY);
    chainMaxZ = Math.max(chainMaxZ, w.startY, w.endY);
  }
  const chainMidX = (chainMinX + chainMaxX) / 2;
  const chainMidZ = (chainMinZ + chainMaxZ) / 2;
  let standaloneOffset = 0;
  const gap = 240;

  for (const e of floorElevs) {
    if (visited.has(e.id)) continue;
    const ep = resolvePanels(e);
    const lj = cornerJointWidth(e.leftCorner ?? 'continuous', jointWidth);
    const rj = cornerJointWidth(e.rightCorner ?? 'continuous', jointWidth);
    const ww = wallWidth(ep, jointWidth, lj, rj);
    let sx: number, sz: number, srot: number;
    if (e.worldX !== undefined && e.worldZ !== undefined) {
      sx = e.worldX; sz = e.worldZ;
      srot = e.worldRotation ?? (DIR_ANGLE[e.direction] ?? 0);
    } else {
      srot = DIR_ANGLE[e.direction] ?? 0;
      switch (e.direction) {
        case 'R': sx = chainMaxX + gap;    sz = chainMidZ - ww / 2; break;
        case 'L': sx = chainMinX - gap;    sz = chainMidZ + ww / 2; break;
        case 'B': sx = chainMidX - ww / 2; sz = chainMinZ - gap;    break;
        case 'F': sx = chainMidX + ww / 2; sz = chainMaxZ + gap;    break;
        default:  sx = chainMaxX + gap + standaloneOffset; sz = chainMidZ; break;
      }
      standaloneOffset += ww + gap;
    }
    result.push({ elevId: e.id, name: e.name, panels: ep, x: sx, z: sz, rotationY: srot, leftEndJoint: lj, rightEndJoint: rj, baseElevation, floorId });
  }
  return result;
}

export default function BracePlanView() {
  const {
    project,
    ui,
    bracingViewMode,
    toggleSidebar,
    toggleProps,
    bracePlanSettings,
    checkedBracePanelIds,
    bracePanelOverrides,
    setBracePlanSettings,
    toggleBracePanel,
    setBraceAllPanels,
    setBracePanelOverride,
    clearBracePanelOverride,
  } = useShallowStore(s => ({
    project: s.project,
    ui: s.ui,
    bracingViewMode:     s.ui.bracingViewMode,
    toggleSidebar: s.toggleSidebar,
    toggleProps: s.toggleProps,
    bracePlanSettings: s.ui.bracePlanSettings,
    checkedBracePanelIds: s.ui.checkedBracePanelIds,
    bracePanelOverrides: s.ui.bracePanelOverrides,
    setBracePlanSettings: s.setBracePlanSettings,
    toggleBracePanel: s.toggleBracePanel,
    setBraceAllPanels: s.setBraceAllPanels,
    setBracePanelOverride: s.setBracePanelOverride,
    clearBracePanelOverride: s.clearBracePanelOverride,
  }));

  const [sidebarW, setSidebarW] = useState(() => Math.round(window.innerWidth * 0.2));
  const [propsW, setPropsW]     = useState(() => Math.round(window.innerWidth * 0.2));
  const [activeBraceFloorId, setActiveBraceFloorId] = useState<string | null>(null);

  const onSidebarResize = useCallback((dx: number) => setSidebarW(w => Math.max(80, Math.min(400, w + dx))), []);
  const onPropsResize   = useCallback((dx: number) => setPropsW(w => Math.max(80, Math.min(500, w - dx))), []);

  if (!project) return null;

  const allPanels     = project.panels;
  const allElevations = project.elevations ?? [];
  const allFloors     = project.floors ?? [];
  const jointWidth    = project.settings.jointWidth ?? 0.75;

  if (allFloors.length === 0 || allPanels.length === 0) {
    return (
      <div className="site-view" style={{ alignItems: 'center', justifyContent: 'center', flex: 1 }}>
        <div style={{ textAlign: 'center', maxWidth: 480, padding: 32 }}>
          <h2 style={{ marginBottom: 12 }}>Brace Plan</h2>
          <p style={{ color: 'var(--text-muted, #888)', lineHeight: 1.5 }}>
            {allFloors.length === 0
              ? 'Add a floor and place panels in Building mode before planning braces.'
              : 'Place panels on elevations before planning braces.'}
          </p>
        </div>
      </div>
    );
  }

  // Active floor defaults to first floor
  const activeFloor = allFloors.find(f => f.id === activeBraceFloorId) ?? allFloors[0] ?? null;

  // ── Active floor data ──────────────────────────────────────────────────────
  const { activeSegments, baseElevation, slabVertices } = useMemo(() => {
    if (!activeFloor || allFloors.length === 0) return { activeSegments: [] as WallSegment[], baseElevation: 0, slabVertices: undefined as [number, number][] | undefined };

    const baseMap = computeFloorBaseElevations(allFloors, allElevations, allPanels);
    const base = baseMap.get(activeFloor.id) ?? 0;
    const floorElevIds = new Set(activeFloor.elevationIds);
    const floorElevs = allElevations.filter(e => floorElevIds.has(e.id));

    const segs = buildFloorSegments(floorElevs, allPanels, jointWidth, base, activeFloor.id);

    // Slab footprint from closed loop
    const fp = computeFootprint(floorElevs, allPanels, jointWidth);
    const vertices = fp.isClosed && fp.corners.length >= 3
      ? fp.corners.map(c => [c.x, c.y] as [number, number])
      : undefined;

    return { activeSegments: segs, baseElevation: base, slabVertices: vertices };
  }, [activeFloor, allFloors, allElevations, allPanels, jointWidth]);

  // ── Sidebar: panels for active floor grouped by elevation ─────────────────
  const sidebarGroups = useMemo((): ElevationGroup[] => {
    if (!activeFloor) return [];
    const floorElevIds = new Set(activeFloor.elevationIds);
    return allElevations
      .filter(e => floorElevIds.has(e.id) && e.panelIds.length > 0)
      .map(e => ({
        elevId: e.id,
        elevName: e.name,
        panels: e.panelIds
          .map(id => allPanels.find(p => p.id === id))
          .filter((p): p is PanelData => p != null),
      }));
  }, [activeFloor, allElevations, allPanels]);

  const floorPanelIds = useMemo(
    () => sidebarGroups.flatMap(g => g.panels.map(p => p.id)),
    [sidebarGroups],
  );
  const checkedSet = new Set(checkedBracePanelIds);

  // Auto-select all panels when floor changes
  useEffect(() => {
    if (floorPanelIds.length > 0) setBraceAllPanels(floorPanelIds, true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFloor?.id]);

  function handleToggleElevation(ids: string[], allChecked: boolean) {
    if (allChecked) {
      const remove = new Set(ids);
      setBraceAllPanels(checkedBracePanelIds.filter(id => !remove.has(id)), true);
    } else {
      setBraceAllPanels([...new Set([...checkedBracePanelIds, ...ids])], true);
    }
  }

  // ── Brace stats ────────────────────────────────────────────────────────────
  const braces = useMemo(
    () => computeBraces(activeSegments, allPanels, checkedSet, bracePlanSettings, bracePanelOverrides, jointWidth),
    [activeSegments, allPanels, checkedSet, bracePlanSettings, bracePanelOverrides, jointWidth],
  );
  const clashCount      = braces.filter(b => b.isClash).length;
  const clashBraceBrace = braces.filter(b => b.isClash && b.clashType === 'brace-brace').length;
  const clashBracePanel = braces.filter(b => b.isClash && b.clashType === 'brace-panel').length;

  return (
    <div className="site-view">
      <aside
        className={`sidebar${ui.sidebarOpen ? '' : ' collapsed'}`}
        style={ui.sidebarOpen ? { width: sidebarW, minWidth: sidebarW } : undefined}
      >
        {/* ── Floor switcher ─────────────────────────────────────────────── */}
        {allFloors.length > 1 && ui.sidebarOpen && (
          <div className="floor-switcher">
            {allFloors.map(f => (
              <button
                key={f.id}
                className={`floor-tab${f.id === activeFloor?.id ? ' active' : ''}`}
                onClick={() => setActiveBraceFloorId(f.id)}
                title={f.type === 'mezzanine' ? `Mezzanine` : f.name}
              >
                {f.name}
              </button>
            ))}
          </div>
        )}

        <BraceSidebar
          groups={sidebarGroups}
          checkedPanelIds={checkedSet}
          overrides={bracePanelOverrides}
          onToggle={toggleBracePanel}
          onToggleElevation={handleToggleElevation}
          onSelectAll={() => setBraceAllPanels(floorPanelIds, true)}
          onDeselectAll={() => setBraceAllPanels(floorPanelIds.length > 0 ? checkedBracePanelIds.filter(id => !floorPanelIds.includes(id)) : [], true)}
          collapsed={!ui.sidebarOpen}
          onToggleCollapse={toggleSidebar}
        />
      </aside>

      {ui.sidebarOpen && <ResizeHandle onResize={onSidebarResize} />}

      <div className="site-view-viewport">
        {bracingViewMode === '2d' ? (
          <BracePlan2D
            segments={activeSegments}
            slabVertices={slabVertices}
            braces={braces}
            allPanels={allPanels}
            checkedPanelIds={checkedSet}
            jointWidth={jointWidth}
          />
        ) : (
          <BraceViewport3D
            segments={activeSegments}
            allPanels={allPanels}
            checkedPanelIds={checkedSet}
            globalSettings={bracePlanSettings}
            panelOverrides={bracePanelOverrides}
            jointWidth={jointWidth}
            slabVertices={slabVertices}
            baseElevation={baseElevation}
          />
        )}
      </div>

      {ui.propsOpen && <ResizeHandle onResize={onPropsResize} />}

      <aside
        className={`props-panel${ui.propsOpen ? '' : ' collapsed'}`}
        style={ui.propsOpen ? { width: propsW, minWidth: propsW } : undefined}
      >
        <BracePropsPanel
          globalSettings={bracePlanSettings}
          onChangeGlobal={setBracePlanSettings}
          checkedPanelIds={checkedSet}
          allPanels={allPanels}
          overrides={bracePanelOverrides}
          onSetOverride={setBracePanelOverride}
          onClearOverride={clearBracePanelOverride}
          braceCount={braces.length}
          clashCount={clashCount}
          clashBraceBrace={clashBraceBrace}
          clashBracePanel={clashBracePanel}
          collapsed={!ui.propsOpen}
          onToggleCollapse={toggleProps}
        />
      </aside>
    </div>
  );
}
